import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { StatCard } from "./Shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import {
  Rocket, CheckCircle2, Loader2, AlertTriangle, ListChecks, Bug, FlaskConical,
  Pencil, History, ChevronDown, ShieldAlert, Target, Clock,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  da_sviluppare: "text-red-400 border-red-500/40 bg-red-500/10",
  in_sviluppo: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  sviluppato: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  testato: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  integrato: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  validato: "text-emerald-300 border-emerald-400/50 bg-emerald-500/15",
  pronto_go_live: "text-[#00E676] border-[#00E676]/60 bg-[#00E676]/15",
};
const PRIO_COLOR = { P0: "bg-red-500/20 text-red-300 border-red-500/40", P1: "bg-amber-500/20 text-amber-300 border-amber-500/40", P2: "bg-blue-500/20 text-blue-300 border-blue-500/40" };
const barColor = (p) => (p >= 90 ? "#00E676" : p >= 60 ? "#F59E0B" : p >= 30 ? "#3B82F6" : "#EF4444");
const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const FIELD_LABEL = { status: "Stato", percent: "Percentuale", note: "Note", open_bugs: "Bug aperti", tests_passed: "Test superati", priority: "Priorità", blockers: "Blocchi", missing: "Task mancanti", done: "Task completati" };

function linesToArr(t) { return t.split("\n").map((x) => x.trim()).filter(Boolean); }
function fmtVal(v) { return Array.isArray(v) ? (v.join(", ") || "—") : String(v ?? "—"); }

export default function RoadmapDashboard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // module object being edited
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ceo/roadmap").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento roadmap…</div>;

  const onSaved = (fresh) => { setData(fresh); setEditing(null); };

  return (
    <div className="space-y-6" data-testid="roadmap-dashboard">
      {/* HERO overall */}
      <div className="bg-gradient-to-br from-[#101014] to-[#16161b] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Rocket className="h-6 w-6 text-[#00E676]" />
            <div>
              <h2 className="font-display text-xl font-bold">Roadmap Go Live · Produzione</h2>
              <p className="text-sm text-zinc-400">Monitoraggio dei {data.modules_total} macro-moduli · ultimo aggiornamento {fmtDate(data.updated_at)}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-[#00E676]" data-testid="roadmap-overall">{data.overall_percent}%</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">avanzamento complessivo</div>
          </div>
        </div>
        <div className="mt-4"><Progress value={data.overall_percent} className="h-2" /></div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="rm-ready" label="Pronti al Go Live" value={`${data.modules_ready}/${data.modules_total}`} icon={CheckCircle2} accent />
        <StatCard testid="rm-indev" label="In sviluppo" value={data.modules_in_development} icon={Loader2} />
        <StatCard testid="rm-blocked" label="Bloccati" value={data.modules_blocked} icon={ShieldAlert} accent={data.modules_blocked > 0} />
        <StatCard testid="rm-bugs" label="Bug aperti" value={data.open_bugs} icon={Bug} accent={data.open_bugs > 0} />
        <StatCard testid="rm-tests" label="Test superati" value={data.tests_passed} icon={FlaskConical} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Next recommended */}
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5" data-testid="roadmap-next">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><Target className="h-4 w-4 text-[#00E676]" /> Prossimo modulo consigliato</h3>
          {data.next_recommended ? (
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-bold text-lg">{data.next_recommended.name}</span>
                <Badge className={`border ${PRIO_COLOR[data.next_recommended.priority]}`}>{data.next_recommended.priority}</Badge>
                <Badge className={`border ${STATUS_COLOR[Object.keys(STATUS_COLOR).find((k) => data.next_recommended.status_label?.includes(""))] || ""}`}>{data.next_recommended.status_label}</Badge>
              </div>
              <div className="text-sm text-zinc-400 mt-2">Completamento: <span className="text-white">{data.next_recommended.percent}%</span></div>
              <div className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">Da completare</div>
              <ul className="text-sm text-zinc-300 list-disc pl-5 mt-1 space-y-0.5">{data.next_recommended.missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
              <div className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">Test</div>
              <div className="text-sm text-zinc-300">{data.next_recommended.tests}</div>
            </div>
          ) : <div className="text-sm text-[#00E676]">🎉 Tutti i moduli sono pronti al Go Live!</div>}
        </div>

        {/* Go Live checklist */}
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5" data-testid="roadmap-checklist">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><ListChecks className="h-4 w-4 text-[#00E676]" /> Checklist Go Live</h3>
          <ul className="space-y-2">
            {data.checklist.map((c) => (
              <li key={c.key} className="flex items-center gap-2 text-sm">
                {c.done ? <CheckCircle2 className="h-4 w-4 text-[#00E676] shrink-0" /> : <div className="h-4 w-4 rounded-full border-2 border-zinc-600 shrink-0" />}
                <span className={c.done ? "text-zinc-200" : "text-zinc-400"}>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Blockers P0 */}
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5" data-testid="roadmap-blockers">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-amber-400" /> Blocker P0 aperti ({data.blockers.length})</h3>
          {data.blockers.length === 0 ? <div className="text-sm text-[#00E676]">Nessun blocker P0.</div> : (
            <ul className="space-y-2">
              {data.blockers.map((b) => (
                <li key={b.id} className="text-sm border-l-2 border-red-500/50 pl-3">
                  <div className="font-medium">{b.name} <span className="text-zinc-500">· {b.status_label}</span></div>
                  {(b.blockers || []).length > 0 && <div className="text-xs text-red-300">{b.blockers.join(" · ")}</div>}
                  {b.open_bugs > 0 && <div className="text-xs text-amber-300">{b.open_bugs} bug aperti</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Status distribution */}
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex flex-wrap gap-2" data-testid="roadmap-distribution">
        {data.status_steps.map((s) => (
          <Badge key={s.key} className={`border ${STATUS_COLOR[s.key]}`}>{s.label}: {data.by_status[s.key] || 0}</Badge>
        ))}
      </div>

      {/* Modules grid */}
      <div>
        <h3 className="font-semibold mb-3">Moduli ({data.modules_total})</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.modules.map((m) => <ModuleCard key={m.id} m={m} onEdit={() => setEditing(m)} />)}
        </div>
      </div>

      {editing && <EditDialog m={editing} statusSteps={data.status_steps} priorities={data.priorities} onClose={() => setEditing(null)} onSaved={onSaved} saving={saving} setSaving={setSaving} />}
    </div>
  );
}

function ModuleCard({ m, onEdit }) {
  const [openHist, setOpenHist] = useState(false);
  return (
    <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5" data-testid={`roadmap-module-${m.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-base">{m.name}</span>
            <Badge className={`border ${PRIO_COLOR[m.priority]}`} data-testid={`rm-prio-${m.id}`}>{m.priority}</Badge>
          </div>
          <Badge className={`border mt-2 ${STATUS_COLOR[m.status]}`} data-testid={`rm-status-${m.id}`}>{m.status_label}</Badge>
        </div>
        <Button size="sm" variant="secondary" onClick={onEdit} data-testid={`rm-edit-${m.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Modifica</Button>
      </div>

      <div className="mt-3">
        <div className="flex justify-between text-xs text-zinc-400 mb-1"><span>Completamento</span><span data-testid={`rm-percent-${m.id}`}>{m.percent}%</span></div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${m.percent}%`, background: barColor(m.percent) }} /></div>
      </div>

      {m.warnings?.length > 0 && (
        <div className="mt-3 space-y-1" data-testid={`rm-warnings-${m.id}`}>
          {m.warnings.map((w, i) => <div key={i} className="flex items-start gap-1.5 text-xs text-amber-300"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}</div>)}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="bg-white/5 rounded p-2"><div className="text-xs text-zinc-500">Bug aperti</div><div className={`font-bold ${m.open_bugs > 0 ? "text-amber-400" : "text-zinc-200"}`}>{m.open_bugs}</div></div>
        <div className="bg-white/5 rounded p-2"><div className="text-xs text-zinc-500">Test ok</div><div className="font-bold text-zinc-200">{m.tests_passed}</div></div>
        <div className="bg-white/5 rounded p-2"><div className="text-xs text-zinc-500">Rischio</div><div className="font-bold text-zinc-200 text-xs truncate" title={m.risks}>{m.risks}</div></div>
      </div>

      {m.deps?.length > 0 && <div className="mt-3 text-xs text-zinc-400"><span className="text-zinc-500">Dipendenze:</span> {m.deps.join(", ")}</div>}
      {m.eta && <div className="mt-1 text-xs text-zinc-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {m.eta}</div>}

      {m.done?.length > 0 && (
        <div className="mt-3"><div className="text-xs text-zinc-500 uppercase tracking-widest mb-1">Task completati</div>
          <ul className="text-xs text-emerald-300/90 space-y-0.5">{m.done.map((t, i) => <li key={i} className="flex items-start gap-1"><CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" /> {t}</li>)}</ul></div>
      )}
      {m.missing?.length > 0 && (
        <div className="mt-2"><div className="text-xs text-zinc-500 uppercase tracking-widest mb-1">Task mancanti</div>
          <ul className="text-xs text-zinc-300 list-disc pl-4 space-y-0.5">{m.missing.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
      )}
      {m.blockers?.length > 0 && (
        <div className="mt-2"><div className="text-xs text-red-400 uppercase tracking-widest mb-1">Blocchi</div>
          <ul className="text-xs text-red-300 list-disc pl-4 space-y-0.5">{m.blockers.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
      )}
      {m.note && <div className="mt-2 text-xs text-zinc-300 bg-white/5 rounded p-2 whitespace-pre-wrap">{m.note}</div>}

      <div className="mt-2 text-[11px] text-zinc-500">Ultimo aggiornamento: {m.history?.[0] ? `${fmtDate(m.history[0].ts)} · ${m.history[0].user_name || m.history[0].user}` : "—"}</div>

      <Collapsible open={openHist} onOpenChange={setOpenHist} className="mt-2">
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid={`rm-history-toggle-${m.id}`}>
            <History className="h-3.5 w-3.5" /> Storico modifiche ({m.history?.length || 0}) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openHist ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-2 max-h-56 overflow-y-auto" data-testid={`rm-history-${m.id}`}>
            {(m.history || []).length === 0 && <div className="text-xs text-zinc-500">Nessuna modifica registrata.</div>}
            {(m.history || []).map((h, i) => (
              <div key={i} className="text-xs border-l-2 border-white/10 pl-2">
                <div className="text-zinc-400">{fmtDate(h.ts)} · <span className="text-zinc-300">{h.user_name || h.user}</span></div>
                {h.changes.map((c, j) => (
                  <div key={j} className="text-zinc-400">
                    <span className="text-zinc-500">{FIELD_LABEL[c.field] || c.field}:</span>{" "}
                    <span className="text-red-300 line-through">{fmtVal(c.old)}</span> → <span className="text-[#00E676]">{fmtVal(c.new)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function EditDialog({ m, statusSteps, priorities, onClose, onSaved, saving, setSaving }) {
  const [form, setForm] = useState({
    status: m.status, percent: String(m.percent), priority: m.priority,
    open_bugs: String(m.open_bugs), tests_passed: String(m.tests_passed),
    note: m.note || "", blockers: (m.blockers || []).join("\n"),
    missing: (m.missing || []).join("\n"), done: (m.done || []).join("\n"),
  });
  const [confirm, setConfirm] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const diffs = useMemo(() => {
    const out = [];
    const cur = { status: m.status, percent: m.percent, priority: m.priority, open_bugs: m.open_bugs, tests_passed: m.tests_passed, note: m.note || "", blockers: m.blockers || [], missing: m.missing || [], done: m.done || [] };
    const next = { status: form.status, percent: parseInt(form.percent || 0, 10), priority: form.priority, open_bugs: parseInt(form.open_bugs || 0, 10), tests_passed: parseInt(form.tests_passed || 0, 10), note: form.note, blockers: linesToArr(form.blockers), missing: linesToArr(form.missing), done: linesToArr(form.done) };
    for (const k of Object.keys(next)) {
      if (JSON.stringify(cur[k]) !== JSON.stringify(next[k])) out.push({ field: k, old: cur[k], new: next[k] });
    }
    return out;
  }, [form, m]);

  const pct = parseInt(form.percent || 0, 10);
  const bugs = parseInt(form.open_bugs || 0, 10);
  const tests = parseInt(form.tests_passed || 0, 10);
  const localErrors = [];
  if (pct < 0 || pct > 100 || isNaN(pct)) localErrors.push("La percentuale deve essere tra 0 e 100.");
  if (form.status === "pronto_go_live" && linesToArr(form.blockers).length > 0) localErrors.push("Non puoi impostare «Pronto al Go Live» con blocchi aperti.");
  if (form.status === "pronto_go_live" && bugs > 0) localErrors.push("Non puoi impostare «Pronto al Go Live» con bug aperti.");
  const rankIdx = statusSteps.findIndex((s) => s.key === form.status);
  const validIdx = statusSteps.findIndex((s) => s.key === "validato");
  if (rankIdx >= validIdx && tests < 1) localErrors.push("Non puoi validare senza test superati (≥ 1).");

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status, percent: pct, priority: form.priority, open_bugs: bugs, tests_passed: tests,
        note: form.note, blockers: linesToArr(form.blockers), missing: linesToArr(form.missing), done: linesToArr(form.done),
      };
      const { data } = await api.put(`/ceo/roadmap/${m.id}`, payload);
      toast.success("Modifiche salvate");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
      setConfirm(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141417] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="roadmap-edit-dialog">
        <DialogHeader><DialogTitle>Modifica modulo · {m.name}</DialogTitle><DialogDescription className="text-zinc-500">Aggiorna stato, percentuale, priorità, bug, test, blocchi e note. Le modifiche vengono tracciate nello storico.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400">Stato</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>{statusSteps.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Priorità</label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Percentuale (0-100)</label>
              <Input type="number" min="0" max="100" value={form.percent} onChange={(e) => set("percent", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-percent" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-sm text-zinc-400">Bug aperti</label><Input type="number" min="0" value={form.open_bugs} onChange={(e) => set("open_bugs", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-bugs" /></div>
              <div><label className="text-sm text-zinc-400">Test superati</label><Input type="number" min="0" value={form.tests_passed} onChange={(e) => set("tests_passed", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-tests" /></div>
            </div>
          </div>
          <div><label className="text-sm text-zinc-400">Blocchi (uno per riga)</label><Textarea rows={2} value={form.blockers} onChange={(e) => set("blockers", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-blockers" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-sm text-zinc-400">Task completati (uno per riga)</label><Textarea rows={3} value={form.done} onChange={(e) => set("done", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-done" /></div>
            <div><label className="text-sm text-zinc-400">Task mancanti (uno per riga)</label><Textarea rows={3} value={form.missing} onChange={(e) => set("missing", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-missing" /></div>
          </div>
          <div><label className="text-sm text-zinc-400">Note</label><Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="edit-note" /></div>

          {localErrors.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/40 rounded p-3 space-y-1" data-testid="edit-errors">
              {localErrors.map((e, i) => <div key={i} className="text-xs text-red-300 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {e}</div>)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="edit-cancel">Annulla</Button>
          <Button className="bg-[#00E676] text-black" disabled={diffs.length === 0 || localErrors.length > 0 || saving} onClick={() => setConfirm(true)} data-testid="edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Salva modifiche
          </Button>
        </DialogFooter>

        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent className="bg-[#141417] border-white/10" data-testid="confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Confermi le modifiche a «{m.name}»?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="text-zinc-400">
                  <div className="mb-2">Verranno registrate nello storico ({diffs.length} campi):</div>
                  <ul className="space-y-1 text-xs">
                    {diffs.map((c, i) => (
                      <li key={i}><span className="text-zinc-500">{FIELD_LABEL[c.field] || c.field}:</span> <span className="text-red-300">{fmtVal(c.old)}</span> → <span className="text-[#00E676]">{fmtVal(c.new)}</span></li>
                    ))}
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">Rivedi</AlertDialogCancel>
              <AlertDialogAction className="bg-[#00E676] text-black" onClick={doSave} data-testid="confirm-save">Conferma e salva</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
